/* eslint-disable react-hooks/exhaustive-deps */
import { Box, Grid, Typography } from "@mui/material";
import React, { useContext, useEffect } from "react";

import { Header, LoginHeader } from "../../components/Header";
import UserContext from "../../context/User";
import useStyle from "./styles";
import Analytics from "../../utils/analytics";
import Seo from "../../components/Seo/Seo";

function Section({ title, children, classes }) {
  return (
    <Box sx={{ mt: 4 }}>
      <Typography variant="subtitle2" className={classes.boldText} sx={{ textTransform: "uppercase", letterSpacing: 0.5, mb: 1.5 }}>
        {title}
      </Typography>
      {children}
    </Box>
  );
}

function P({ children, classes }) {
  return (
    <Typography variant="subtitle2" className={classes.MV2} sx={{ lineHeight: 1.7 }}>
      {children}
    </Typography>
  );
}

function Bullets({ items, classes }) {
  return (
    <ul className={classes.bullet}>
      {items.map((item, i) => (
        <li key={i}>
          <Typography variant="subtitle2" sx={{ lineHeight: 1.7 }}>{item}</Typography>
        </li>
      ))}
    </ul>
  );
}

function PrivacyPolicy() {
  useEffect(() => {
    Analytics.track(Analytics.events.NAVIGATE_TO_PRIVACY_POLICY);
  }, []);
  const classes = useStyle();
  const { isLoggedIn } = useContext(UserContext);

  return (
    <Grid className={classes.root}>
      <Seo
        title="Privacy Policy"
        description="How RushMyFood collects, uses and protects your personal information when you order food through our site and apps."
        path="/privacy"
      />
      {isLoggedIn ? <Header /> : <LoginHeader showIcon />}
      <Grid container className={classes.mainContainer}>
        <Box className={classes.imageContainer}>
          <Typography variant="h4" component="h1" color="textPrimary" align="center" className={classes.title}>
            Privacy Policy
          </Typography>
        </Box>
        <Grid container item xs={12} className={classes.mainContainer}>
          <Grid item xs={1} md={1} />
          <Grid container item xs={10} sm={10} md={9}>
            <Box>
              <P classes={classes}>
                We at RushMyFood.com are concerned about privacy issues and want you to be familiar with how we
                collect, use and disclose Personally Identifiable Information (as defined below). This Privacy Policy
                describes our practices in connection with Personally Identifiable Information that we collect through
                the site from which you are accessing this policy (the “Site”). This Privacy Policy does not address
                the collection, use or disclosure of information by any of our affiliates or by third parties.
              </P>
              <P classes={classes}>
                By using the Site, you agree to the terms and conditions of this Privacy Policy. If you do not agree
                to the terms and conditions of this Privacy Policy, please do not use the Site. We reserve the right
                to change this Privacy Policy without prior notice. You can determine when this Privacy Policy was
                last revised by referring to the “LAST UPDATED” legend at the top of this page. Any changes to our
                Privacy Policy will become effective upon our posting of the revised Privacy Policy on the Site. Use
                of the Site following such changes constitutes your acceptance of the revised Privacy Policy then in
                effect.
              </P>

              <Section title="What Types of Information Do We Collect From You?" classes={classes}>
                <P classes={classes}>
                  Personally Identifiable Information (or “PII”) is information that identifies you as an individual.
                  In order for you to take advantage of particular opportunities provided through the Site (for
                  example, to use for the services offered on the Site), we may require that you furnish PII. We
                  collect PII from you, such as your name, postal address, telephone number, e-mail address. NOTE:
                  Credit card number (used as a payment method) is not stored or saved at all. It is one time use
                  only.
                </P>
                <P classes={classes}>
                  Non-Personally Identifiable Information (or “Non-PII”) is aggregated information, demographic
                  information and any other information that does not reveal your specific identity. We and our third
                  party service providers may collect Non-PII from you, including your MAC address, your computer
                  type, screen resolution, OS version, Internet browser and demographic data, for example your
                  location, gender and date of birth and we may aggregate PII in a manner such that the end-product
                  does not personally identify you or any other user of the Site, for example, by using PII to
                  calculate the percentage of our users who have a particular telephone area code.
                </P>
                <P classes={classes}>
                  IP Addresses are the Internet Protocol addresses of the computers that you are using. Your IP
                  Address is automatically assigned to the computer that you are using by your Internet Service
                  Provider (ISP). This number is identified and logged automatically in our server log files whenever
                  users visit the Site, along with the time(s) of such visit(s) and the page(s) that were visited.
                  Collecting IP Addresses is standard practice on the Internet and is done automatically by many web
                  sites.
                </P>
              </Section>

              <Section title="How Do We Use Information Collected From You?" classes={classes}>
                <Typography variant="subtitle2" className={classes.boldText} sx={{ mt: 1 }}>Personally Identifiable Information.</Typography>
                <P classes={classes}>We may use PII:</P>
                <Bullets
                  classes={classes}
                  items={[
                    "To fulfill your requests, e.g., to allow you to order food through our restaurant partners and have it delivered to you or to provide you with information that you request.",
                    "To send you important information regarding the Site, changes to our terms, conditions, and policies and/or other administrative information. Because this information may be important, you may not opt-out of receiving such communications.",
                    "To inform you of products, programs, services and promotions that we believe may be of interest to you. To fulfill your purchase(s) (e.g., to process credit card payments in connection with your food order).",
                    "To permit you to send messages regarding Site-related content to a friend through the Site. If you wish to use this feature, you may be required to provide us, and we may use, with your friend’s email address to facilitate your sending of such message to your friend. By using this functionality, you affirm that you are entitled to use and provide us with the recipient’s name and email address for this purpose. We will not to use your friend’s PII for any other purpose other than to allow the message to be sent and/or to troubleshoot any issues with respect to the sending of such message, unless we disclose such other purpose to you at the time you provide such PII to us.",
                    "To permit you to participate in sweepstakes, contests and similar promotions (collectively, “Promotions”). You should carefully review the rules, if any, of each Promotion in which you participate, as they may contain additional important information about our use of your PII. To the extent that the terms and conditions of such rules concerning the treatment of your PII conflict with this Privacy Policy, the terms and conditions of such rules shall control.",
                    "To customize content on our Site (e.g., the restaurants that will deliver to your location).",
                    "For our internal business purposes, such as data analysis, audits, developing new products, enhancing our website, improving our services, identifying usage trends and determining the effectiveness of our promotional campaigns.",
                  ]}
                />
                <P classes={classes}>
                  <b>Non-Personally Identifiable Information.</b> Because Non-PII does not personally identify you, we
                  may use such information for any purpose. In addition, we reserve the right to share such Non PII,
                  which does not personally identify you, with our affiliates and with other third parties, for any
                  purpose. In some instances, we may combine Non-PII with PII (such as combining your name with your
                  geographical location). If we do combine any Non-PII with PII, the combined information will be
                  treated by us as PII hereunder as long as it is so combined.
                </P>
                <Typography variant="subtitle2" className={classes.boldText} sx={{ mt: 2 }}>IP Addresses.</Typography>
                <P classes={classes}>
                  We use IP addresses for purposes such as calculating Site usage levels, helping diagnose server
                  problems, and administering the Site. We may also use and disclose IP Addresses for all the
                  purposes for which we use and disclose PII. Please note that we treat IP Addresses, server log
                  files and related information as Non-PII, except where we are required to do otherwise under
                  applicable law.
                </P>
              </Section>

              <Section title="How Is Personally Identifiable Information Disclosed?" classes={classes}>
                <P classes={classes}>We may disclose PII:</P>
                <Bullets
                  classes={classes}
                  items={[
                    "To our third party service providers who provide services such as website hosting, data analysis, payment processing, order fulfillment, infrastructure provision, IT services, customer service, e-mail delivery services, credit card processing and other similar services to enable them to provide services.",
                    "As required to fulfill your purchase (e.g., to our restaurant partners to enable them to fulfill your order).",
                    "To identify you to any person to whom you send messages regarding Site-related content through the Site.",
                    "To third parties sponsors of Promotions (irrespective of whether such Promotions are hosted by us), or otherwise in accordance with the rules applicable to such Promotion. You should carefully review the rules, if any, of each Promotion in which you participate, as they may contain additional important information about a sponsor’s or other third parties’ use of your PII. To the extent that the terms and conditions of such rules concerning the treatment of your PII conflict with this Privacy Policy, the terms and conditions of such rules shall control.",
                    "To an affiliate or other third party in the event of any reorganization, merger, sale, joint venture, assignment, transfer or other disposition of all or any portion of our business, assets or stock (including without limitation in connection with any bankruptcy or similar proceedings).",
                    "As we believe to be appropriate: (a) under applicable law including laws outside your country of residence; (b) to comply with legal process; (c) to respond to requests from public and government authorities including public and government authorities outside your country of residence; (d) to enforce our terms and conditions; (e) to protect our operations or those of any of our affiliates; (f) to protect our rights, privacy, safety or property, and/or that of our affiliates, you or others; and (g) to permit us to pursue available remedies or limit the damages that we may sustain.",
                  ]}
                />
              </Section>

              <Section title="Other Important Notices Regarding Our Privacy Practices" classes={classes}>
                <P classes={classes}>
                  <b>Forums and Profiles.</b> We may make available through the Site services (for example, message
                  boards, chat functionality, “profile pages” and blogs, among other services) to which you are able
                  to post information and materials. Please note that any information you disclose through such
                  services becomes public information, and may be available to visitors to the Site and to the
                  general public. We urge you to exercise discretion and caution when deciding to disclose your PII,
                  or any other information, on the Site. WE ARE NOT RESPONSIBLE FOR THE USE OF ANY PII YOU
                  VOLUNTARILY DISCLOSE THROUGH THE SITE.
                </P>
                <P classes={classes}>
                  <b>Third Party Sites.</b> This Privacy Policy does not address, and we are not responsible for, the
                  privacy, information or other practices of any third parties, including without limitation any of
                  our affiliates and any third party operating any site to which this Site contains a link. The
                  inclusion of a link on the Site does not imply endorsement of the linked site by us or by our
                  affiliates.
                </P>
                <P classes={classes}>
                  <b>Security.</b> We use reasonable organizational, technical and administrative measures to protect
                  PII under our control. Unfortunately, no data transmission over the Internet or data storage system
                  can be guaranteed to be 100% secure. Please do not send us sensitive information through email. If
                  you have reason to believe that your interaction with us is no longer secure (for example, if you
                  feel that the security of any account you might have with us has been compromised), you must
                  immediately notify us of the problem by contacting us in accordance with the “Contacting Us”
                  section below (note that if you choose to notify us via physical mail, this will delay the time it
                  takes for us to respond to the problem).
                </P>
                <P classes={classes}>
                  <b>Opt-Out.</b> If you change your mind, and no longer wish to receive marketing-related e-mails
                  from us on a going-forward basis, you may opt-out of receiving such marketing-related e-mails or
                  from such sharing by adjusting your preferences in the “My Account” section of the Site.
                </P>
                <P classes={classes}>
                  <b>Changing or Suppressing PII.</b> If you would like to review, correct, update, suppress or
                  otherwise limit our use of your PII that has been previously provided to us, you may do so by
                  adjusting your preferences in the “My Account” section of the Site. We may need to retain certain
                  information for recordkeeping purposes, and there may also be residual information that will remain
                  within our databases and other records, which will not be removed. Finally, we are not responsible
                  for removing or suppressing information from the databases of third parties with whom we have
                  already shared your PII, or limiting their use of your PII.
                </P>
                <P classes={classes}>
                  <b>Retention Period.</b> We will retain your PII for the period necessary to fulfill the purposes
                  outlined in this Privacy Policy unless a longer retention period is required or permitted by law.
                </P>
                <P classes={classes}>
                  <b>Note Regarding the Use of the Site by Children.</b> The Site is not directed to individuals
                  under the age of thirteen (13), and we request that such individuals not provide PII through the
                  Site.
                </P>
                <P classes={classes}>
                  <b>Jurisdictional Issues.</b> We do not represent or warrant that the Site, or any part thereof, is
                  appropriate or available for use in any particular jurisdiction. Those who choose to access the
                  Site do so on their own initiative and at their own risk, and are responsible for complying with
                  all local laws, rules and regulations. We may limit the Site’s availability, in whole or in part,
                  to any person, geographic area or jurisdiction we choose, at any time and in our sole discretion.
                  By using the Site and submitting any PII, you consent to the transfer of PII to other countries,
                  such as the United States, which may provide a different level of data security than in your
                  country of residence.
                </P>
                <P classes={classes}>
                  <b>Contacting Us.</b> If you have any questions regarding this Privacy Policy, please contact us by
                  Email at info@Rushmyfood.com.
                </P>
                <P classes={classes}>
                  Please note that e-mail communications will not necessarily be secure; accordingly you should not
                  include credit card information in your e-mail correspondence with us.
                </P>
                <P classes={classes}>
                  Consuming raw or uncooked meat, poultry, seafood, shellfish, or eggs may increase your risk of food
                  borne illness, especially with certain medical conditions. PLEASE INFORM US IF A PERSON IN YOUR
                  PARTY HAS A FOOD ALLERGY
                </P>
              </Section>
            </Box>
          </Grid>
        </Grid>
      </Grid>
    </Grid>
  );
}

export default React.memo(PrivacyPolicy);
